import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Canvas, useThree, type ThreeEvent } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Grid, ContactShadows, Html } from '@react-three/drei';
import {
  RotateCw,
  Undo2,
  Redo2,
  Save,
  Wand2,
  PackageOpen,
  Trash2,
  AlertTriangle,
  Boxes,
  Loader2,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { ContainerWireframe } from './container-wireframe';
import { DraggableCargoBox, GhostBox } from './draggable-cargo-box';
import { useContainerLayout } from '../hooks/use-container-layout';
import { agruparUnidades, idDeLinea } from '../utils/unit-positions';
import { packContainer } from '../utils/bin-packing';
import { summarizeUnplaced } from '../utils/container-freight-proration';
import {
  computeLoadingSequence,
  describeProblem,
  DEFAULT_GRID_CM,
  type ContainerBounds,
  type LayoutBox,
  type PlacementCheck,
} from '../utils/container-layout';
import type {
  ContainerType,
  ContainerItem,
  ContainerStatus,
  BulkPositionUpdate,
} from '../types/import-tracking.types';

/**
 * Editor 3D de acomodo de carga.
 *
 * Permite arrastrar, rotar y apilar la carga dentro del contenedor con
 * validación en vivo: cada movimiento se contrasta contra las paredes, las
 * demás cajas y el peso máximo antes de aplicarse.
 *
 * La lógica de acomodo vive en `utils/container-layout.ts` y el estado en
 * `hooks/use-container-layout.ts`, ambos testeados sin three.js. Este
 * componente solo se ocupa de la escena y la interacción.
 */

export interface ContainerEditor3DProps {
  containerType: ContainerType;
  items: ContainerItem[];
  /** Estado del contenedor — define si las puertas se dibujan abiertas. */
  status?: ContainerStatus;
  /**
   * Persiste el acomodo. Debe rechazar la promesa si falla.
   *
   * `positions` describe el acomodo completo: los items que no aparecen quedan
   * sin ubicar (los de la bandeja).
   */
  onSave: (payload: BulkPositionUpdate) => Promise<void>;
  /** Solo lectura: se puede mirar y rotar la cámara, pero no editar. */
  readOnly?: boolean;
  /**
   * Abre el alta del contenedor siguiente.
   *
   * Se ofrece **solo** cuando hay carga en la bandeja, que es el momento en que
   * hace falta: el aviso de "no entra" pasa de nombrar la solución a permitirla.
   * Si no viene, el botón no se dibuja.
   */
  onCrearContenedor?: () => void;
  className?: string;
}

// ── Conversión entre el modelo de datos y el del acomodo ─────────────────────

/**
 * Una línea del contenedor son **N bultos**, no uno.
 *
 * 🔴 Acá estaba el bug que rompía todo el acomodo. Cada `ContainerItem` se
 * convertía en UNA caja, sin mirar `quantity`: una línea de 200 teclados
 * entraba a la escena como un solo teclado. Los síntomas que se veían en
 * pantalla —"se pone 1 de cada producto", "al guardar se borra todo", el
 * auto-acomodo que "queda bien" pero deja el contenedor vacío— eran todos el
 * mismo problema. Y la ocupación tampoco podía dar bien: se acomodaban 6
 * bultos en un contenedor que lleva 470.
 *
 * El id de cada unidad es `<itemId>#<n>`, así se puede arrastrar una sola y
 * al guardar se agrupan de vuelta por línea.
 */
export function idDeUnidad(itemId: string, indice: number): string {
  return `${itemId}#${indice}`;
}

/** Tope de bultos dibujados por línea, para que 5.000 unidades no cuelguen. */
const MAX_BULTOS_POR_LINEA = 500;

function toLayoutBoxes(item: ContainerItem): LayoutBox[] {
  const rotated = item.isRotated;
  const cantidad = Math.max(1, Math.min(item.quantity || 1, MAX_BULTOS_POR_LINEA));

  const guardadas = Array.isArray(item.unitPositions) ? item.unitPositions : null;

  return Array.from({ length: cantidad }, (_, i) => {
    const u = guardadas?.[i];
    return {
      id: idDeUnidad(item.id, i),
      // Cada unidad recuerda dónde quedó. Sin acomodo fino guardado, todas
      // arrancan en la posición de la línea y el auto-acomodo las separa.
      x: u?.x ?? item.positionX ?? 0,
      y: u?.y ?? item.positionY ?? 0,
      z: u?.z ?? item.positionZ ?? 0,
      // Las dimensiones se guardan sin rotar; acá se aplican.
      lengthCm: (u?.r ?? rotated) ? item.widthCm : item.lengthCm,
      widthCm: (u?.r ?? rotated) ? item.lengthCm : item.widthCm,
      heightCm: item.heightCm,
      rotated: u?.r ?? rotated,
      weightKg: item.weightKg,
    };
  });
}

/**
 * Agrupa las unidades de vuelta en una posición por línea.
 *
 * Usa el mismo `agruparUnidades` que el guardado del auto-empaque: dos copias
 * de esta lógica es exactamente cómo se llega a que un camino guarde bien y el
 * otro pierda el acomodo, que fue el bug.
 */
function agruparPorLinea(cajas: LayoutBox[]): BulkPositionUpdate['positions'] {
  return agruparUnidades(
    cajas.map((c) => ({ id: c.id, x: c.x, y: c.y, z: c.z, rotated: c.rotated }))
  );
}

// ── Plano de arrastre ────────────────────────────────────────────────────────

/**
 * Plano invisible sobre el piso del contenedor que captura el puntero mientras
 * se arrastra. Sin él no habría contra qué proyectar el rayo del mouse.
 */
function DragPlane({
  bounds,
  active,
  onMove,
  onRelease,
}: {
  bounds: ContainerBounds;
  active: boolean;
  onMove: (x: number, y: number) => void;
  onRelease: () => void;
}) {
  const { camera, raycaster, pointer } = useThree();
  const plane = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0));
  const target = useRef(new THREE.Vector3());

  useEffect(() => {
    if (!active) return;

    const handleMove = () => {
      raycaster.setFromCamera(pointer, camera);
      if (raycaster.ray.intersectPlane(plane.current, target.current)) {
        onMove(target.current.x * 100, target.current.z * 100);
      }
    };
    const handleUp = () => onRelease();

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [active, camera, raycaster, pointer, onMove, onRelease]);

  // Plano amplio para que el puntero no se "caiga" al salirse del contenedor.
  const size = Math.max(bounds.lengthCm, bounds.widthCm) / 100 + 20;
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[bounds.lengthCm / 200, 0, bounds.widthCm / 200]}
      visible={false}
    >
      <planeGeometry args={[size, size]} />
    </mesh>
  );
}

// ── Escena ───────────────────────────────────────────────────────────────────

interface SceneProps {
  status: ContainerStatus;
  bounds: ContainerBounds;
  boxes: LayoutBox[];
  colorOf: (id: string) => string;
  labelOf: (id: string) => string;
  selectedId: string | null;
  draggingId: string | null;
  ghost: { box: LayoutBox; check: PlacementCheck } | null;
  readOnly: boolean;
  onSelect: (id: string) => void;
  onDragStart: (id: string, event: ThreeEvent<PointerEvent>) => void;
  onDragMove: (x: number, y: number) => void;
  onDragEnd: () => void;
}

function Scene({
  status,
  bounds,
  boxes,
  colorOf,
  labelOf,
  selectedId,
  draggingId,
  ghost,
  readOnly,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
}: SceneProps) {
  const centerX = bounds.lengthCm / 200;
  const centerZ = bounds.widthCm / 200;
  const collisionIds = new Set(ghost?.check.collidesWith ?? []);

  // El encuadre: ver el comentario de `PerspectiveCamera` más abajo.
  const FOV_ESCENA = 45;
  const largo = bounds.lengthCm / 100;
  const alto = bounds.heightCm / 100;
  const distancia =
    (Math.max(largo, alto * 1.6) / (2 * Math.tan(((FOV_ESCENA / 2) * Math.PI) / 180))) * 1.55;
  const POSICION_CAMARA: [number, number, number] = [
    centerX + distancia * 0.62,
    alto + distancia * 0.42,
    centerZ + distancia * 0.72,
  ];

  return (
    <>
      {/*
        La cámara se aleja según el contenedor, no a una distancia fija.

        🔴 Estaba en `[centerX + 7, 6, centerZ + 8]`: siempre a unos 12 unidades
        del centro, sin mirar cuánto mide el contenedor. En un 20' (5,9 m) sobra
        lugar y la carga queda chica en el medio de un lienzo vacío; en un 40'HQ
        (12 m) directamente **no entra**. Es el mismo error que tenía la escena
        del viaje del portal.

        La cuenta: para que algo de largo `L` entre en un campo de `fov` grados
        hace falta `L / (2·tan(fov/2))`, más un margen. Se toma el mayor entre el
        largo y el alto para que sirva igual en un contenedor alto y corto.
      */}
      <PerspectiveCamera makeDefault position={POSICION_CAMARA} fov={FOV_ESCENA} />
      <OrbitControls
        enabled={draggingId === null}
        target={[centerX, bounds.heightCm / 200, centerZ]}
        maxPolarAngle={Math.PI / 2.05}
        minDistance={2}
        maxDistance={40}
        enableDamping
        dampingFactor={0.08}
      />

      {/*
        La misma luz de estudio que el contenedor del portal del cliente: un
        hemisférico que da el cielo y el rebote del piso, una principal alta que
        talla las cajas, un relleno frío para que las sombras no se vayan a negro
        y un contraluz que despega el armazón del fondo.

        Con cajas opacas esto es lo que reemplaza a las aristas: la separación
        entre bultos la hace el sombreado. Con la luz plana anterior, dos cajas
        del mismo color pegadas se veían como un bloque.
      */}
      <hemisphereLight args={['#eaf2fd', '#b6c0cc', 0.95]} />
      <ambientLight intensity={0.5} />
      <directionalLight
        position={[10, 14, 6]}
        intensity={1.3}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={60}
      />
      <directionalLight position={[-8, 6, -6]} intensity={0.4} color="#cfe0f5" />
      <directionalLight position={[0, 4, -10]} intensity={0.28} />

      {/*
        Piso liso, no grilla de depuración.

        🔴 Había una grilla de 40×40 con celdas de 50 cm y líneas de sección
        marcadas. Servía cuando esto era una herramienta de prueba: ayudaba a
        contar centímetros. Con la carga real compite con las cajas por la
        atención y hace que la pantalla se lea como un editor 3D en vez de como
        un contenedor. El portal del cliente apoya la carga en un piso plano y
        claro, y por eso lo que se mira es la carga.

        Se conserva una grilla MUY tenue y acotada al contenedor: sigue dando la
        referencia de escala al arrastrar, sin gritar.
      */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[centerX, -0.004, centerZ]} receiveShadow>
        <planeGeometry args={[largo * 2.6, (bounds.widthCm / 100) * 4]} />
        <meshStandardMaterial color="#eef3fb" roughness={1} />
      </mesh>
      <Grid
        args={[largo * 1.4, (bounds.widthCm / 100) * 1.8]}
        cellSize={0.5}
        cellThickness={0.4}
        cellColor="#dbe3ee"
        sectionSize={2}
        sectionThickness={0.6}
        sectionColor="#c9d4e2"
        position={[centerX, -0.002, centerZ]}
        fadeDistance={distancia * 2.2}
        infiniteGrid={false}
      />
      <ContactShadows
        position={[centerX, 0, centerZ]}
        opacity={0.32}
        scale={25}
        blur={2.2}
        far={6}
      />

      <ContainerWireframe
        lengthCm={bounds.lengthCm}
        widthCm={bounds.widthCm}
        heightCm={bounds.heightCm}
        status={status}
      />

      {boxes.map((box) => (
        <DraggableCargoBox
          key={box.id}
          box={box}
          color={colorOf(box.id)}
          label={labelOf(box.id)}
          isSelected={selectedId === box.id}
          isDragging={draggingId === box.id}
          isCollisionTarget={collisionIds.has(box.id)}
          isDimmed={selectedId !== null && selectedId !== box.id && draggingId === null}
          onSelect={onSelect}
          onDragStart={readOnly ? () => undefined : onDragStart}
        />
      ))}

      {ghost && <GhostBox box={ghost.box} valid={ghost.check.valid} />}

      <DragPlane
        bounds={bounds}
        active={draggingId !== null}
        onMove={onDragMove}
        onRelease={onDragEnd}
      />

      {boxes.length === 0 && (
        <Html position={[centerX, bounds.heightCm / 200, centerZ]} center>
          <div className="text-muted-foreground text-sm whitespace-nowrap select-none pointer-events-none">
            Contenedor vacío
          </div>
        </Html>
      )}
    </>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────

export function ContainerEditor3D({
  containerType,
  items,
  status = 'LOADING',
  onSave,
  readOnly = false,
  onCrearContenedor,
  className,
}: ContainerEditor3DProps) {
  const bounds = useMemo<ContainerBounds>(
    () => ({
      lengthCm: containerType.lengthCm,
      widthCm: containerType.widthCm,
      heightCm: containerType.heightCm,
      maxWeightKg: containerType.maxWeightKg ?? 0,
    }),
    [containerType]
  );

  const { initialPlaced, initialTray } = useMemo(() => {
    const placed: LayoutBox[] = [];
    const tray: LayoutBox[] = [];
    for (const item of items) {
      const cajas = toLayoutBoxes(item);
      if (item.positionX == null) tray.push(...cajas);
      else placed.push(...cajas);
    }
    return { initialPlaced: placed, initialTray: tray };
  }, [items]);

  const layout = useContainerLayout(initialPlaced, initialTray, bounds, DEFAULT_GRID_CM);

  /**
   * Identidad de la carga: qué items hay y cuánto miden.
   *
   * Deliberadamente NO incluye las posiciones. Así, guardar y recargar no
   * reinicia el editor (las posiciones cambian pero la carga es la misma),
   * mientras que agregar, borrar o redimensionar un item sí lo hace.
   */
  const cargoSignature = useMemo(
    () =>
      items
        .map((i) => `${i.id}:${i.lengthCm}x${i.widthCm}x${i.heightCm}:${i.weightKg}`)
        .sort()
        .join('|'),
    [items]
  );
  const lastSignature = useRef(cargoSignature);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [ghost, setGhost] = useState<{ box: LayoutBox; check: PlacementCheck } | null>(null);
  const [saving, setSaving] = useState(false);
  /** `true` mientras el acomodo automático calcula. Ver `handleAutoArrange`. */
  const [arranging, setArranging] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (lastSignature.current === cargoSignature) return;
    lastSignature.current = cargoSignature;
    layout.reset(initialPlaced, initialTray);
    // La selección puede apuntar a un item que ya no existe.
    setSelectedId(null);
    setSaveError(null);
    // `layout` cambia en cada render; la dependencia real es la firma de la carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargoSignature]);

  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  /*
    Se busca por la LÍNEA, no por el id del bulto.

    🔴 Los bultos pasaron a llamarse `<itemId>#<n>` y estas dos búsquedas
    seguían usando el id completo: no encontraban nada y todo caía en el gris
    de respaldo. La carga se veía entera del mismo color y el rótulo decía
    "Carga" en vez del producto.
  */
  const colorOf = useCallback(
    (id: string) => itemById.get(idDeLinea(id))?.color ?? '#64748b',
    [itemById]
  );
  const labelOf = useCallback(
    (id: string) => {
      const item = itemById.get(idDeLinea(id));
      return item?.description ?? item?.material?.name ?? 'Carga';
    },
    [itemById]
  );

  /**
   * Lo que quedó en la bandeja, con la cuenta hecha.
   *
   * **"No entra" sin números no alcanza para decidir.** Lo que necesita saber
   * quien opera es cuántos contenedores más tiene que pedir, que es lo primero
   * que le va a preguntar el cliente.
   *
   * Usa `summarizeUnplaced`, que ya existía **y ya estaba probado** — vivía en el
   * panel alimentando un aviso que nunca se mostraba (ver el comentario en
   * `container-detail-panel.tsx`). Escribir la misma cuenta de nuevo acá habría
   * dejado dos versiones de la misma matemática, que es exactamente el problema
   * que este armado ya tuvo con los dos algoritmos de empaquetado.
   *
   * La capacidad sale del contenedor actual porque el siguiente va a ser igual:
   * es un hermano del mismo embarque.
   *
   * `null` si la bandeja está vacía.
   */
  const sobrante = useMemo(() => {
    if (layout.tray.length === 0) return null;

    /*
      La bandeja tiene una caja por UNIDAD; `summarizeUnplaced` espera líneas con
      cantidad. Se agrupa por la línea de origen para que "12 unidades de un
      producto" no se cuente como doce productos distintos en el detalle.
    */
    const porLinea = new Map<string, { cantidad: number; caja: (typeof layout.tray)[number] }>();
    for (const caja of layout.tray) {
      const linea = idDeLinea(caja.id);
      const previo = porLinea.get(linea);
      if (previo) previo.cantidad += 1;
      else porLinea.set(linea, { cantidad: 1, caja });
    }

    const resumen = summarizeUnplaced({
      unplacedIds: [...porLinea.keys()],
      items: [...porLinea.entries()].map(([id, { cantidad, caja }]) => ({
        id,
        description: labelOf(caja.id),
        quantity: cantidad,
        unitVolumeCbm: (caja.lengthCm * caja.widthCm * caja.heightCm) / 1_000_000,
        unitWeightKg: caja.weightKg,
      })),
      containerVolumeCbm: (bounds.lengthCm * bounds.widthCm * bounds.heightCm) / 1_000_000,
      containerMaxCargoKg: bounds.maxWeightKg,
    });

    return {
      volumenCbm: resumen.totalVolumeCbm,
      pesoKg: resumen.totalWeightKg,
      contenedoresExtra: Math.max(1, resumen.extraContainersNeeded),
    };
  }, [layout.tray, bounds]);

  // ── Arrastre ──
  const handleDragStart = useCallback(
    (id: string, event: ThreeEvent<PointerEvent>) => {
      if (readOnly) return;
      const box = layout.placed.find((b) => b.id === id);
      if (!box) return;
      // Diferencia entre dónde se agarró y el origen de la caja, para que no salte.
      dragOffset.current = {
        x: event.point.x * 100 - box.x,
        y: event.point.z * 100 - box.y,
      };
      setDraggingId(id);
      document.body.style.cursor = 'grabbing';
    },
    [layout.placed, readOnly]
  );

  const handleDragMove = useCallback(
    (worldX: number, worldY: number) => {
      if (!draggingId) return;
      const preview = layout.preview(
        draggingId,
        worldX - dragOffset.current.x,
        worldY - dragOffset.current.y
      );
      setGhost(preview);
    },
    [draggingId, layout]
  );

  const handleDragEnd = useCallback(() => {
    if (draggingId && ghost?.check.valid) {
      layout.moveTo(draggingId, ghost.box.x, ghost.box.y);
    }
    setDraggingId(null);
    setGhost(null);
    document.body.style.cursor = 'auto';
  }, [draggingId, ghost, layout]);

  // ── Teclado ──
  useEffect(() => {
    if (readOnly) return;

    const handler = (e: KeyboardEvent) => {
      // No secuestrar el teclado mientras se escribe en un campo.
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable))
        return;

      const ctrl = e.ctrlKey || e.metaKey;

      if (ctrl && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        layout.undo();
        return;
      }
      if (ctrl && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
        e.preventDefault();
        layout.redo();
        return;
      }
      if (!selectedId) return;

      const step = e.shiftKey ? DEFAULT_GRID_CM * 5 : DEFAULT_GRID_CM;
      switch (e.key) {
        case 'r':
        case 'R':
          e.preventDefault();
          layout.rotate(selectedId);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          layout.nudge(selectedId, -step, 0);
          break;
        case 'ArrowRight':
          e.preventDefault();
          layout.nudge(selectedId, step, 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          layout.nudge(selectedId, 0, -step);
          break;
        case 'ArrowDown':
          e.preventDefault();
          layout.nudge(selectedId, 0, step);
          break;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          layout.unplace(selectedId);
          setSelectedId(null);
          break;
        case 'Escape':
          setSelectedId(null);
          break;
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedId, layout, readOnly]);

  /**
   * Auto-acomodar, con el MISMO motor que usaba el botón "Auto-Empacar".
   *
   * 🔴 Había dos algoritmos distintos para lo mismo: `packContainer` en la
   * vista de solo lectura y `autoArrange` acá. El usuario acomodaba con uno y
   * editaba con el otro, y los resultados no coincidían.
   *
   * Gana `packContainer` porque es el que venía dando buen resultado y porque
   * expande por cantidad de fábrica. Lo que no entra queda en la bandeja, que
   * es donde se ve qué sobró.
   */
  const ejecutarAcomodo = useCallback(() => {
    const porLinea = new Map<string, LayoutBox[]>();
    for (const b of [...layout.placed, ...layout.tray]) {
      const linea = idDeLinea(b.id);
      const lista = porLinea.get(linea);
      if (lista) lista.push(b);
      else porLinea.set(linea, [b]);
    }

    /*
      Las medidas SIN rotar, una sola vez.

      🔴 `toLayoutBoxes` ya devuelve `lengthCm`/`widthCm` con la rotación
      aplicada. Al volver del empaquetado se volvían a intercambiar según
      `p.rotated`, o sea que una caja ya rotada quedaba con las medidas
      cruzadas: el largo donde va el ancho. Con eso el chequeo de paredes se
      hacía sobre un tamaño y el dibujo sobre otro, y las cajas se salían del
      contenedor.
    */
    const base = new Map(
      [...porLinea.entries()].map(([id, unidades]) => {
        const u = unidades[0];
        return [
          id,
          {
            lengthCm: u.rotated ? u.widthCm : u.lengthCm,
            widthCm: u.rotated ? u.lengthCm : u.widthCm,
            heightCm: u.heightCm,
            weightKg: u.weightKg,
          },
        ] as const;
      })
    );

    const result = packContainer(
      {
        lengthCm: bounds.lengthCm,
        widthCm: bounds.widthCm,
        heightCm: bounds.heightCm,
        maxWeightKg: bounds.maxWeightKg,
        maxVolumeCbm: (bounds.lengthCm * bounds.widthCm * bounds.heightCm) / 1_000_000,
      },
      [...porLinea.entries()].map(([id, unidades]) => ({
        id,
        ...base.get(id)!,
        // El peso que espera el empaquetado es el de la LÍNEA: adentro lo
        // divide por la cantidad para sacar el de cada bulto.
        weightKg: base.get(id)!.weightKg * unidades.length,
        quantity: unidades.length,
        color: '#3B82F6',
      }))
    );

    // El resultado vuelve al modelo del editor para poder seguir arrastrando.
    const colocadas: LayoutBox[] = [];
    const usadas = new Map<string, number>();
    for (const p of result.placements) {
      const linea = idDeLinea(p.itemId);
      const n = usadas.get(linea) ?? 0;
      usadas.set(linea, n + 1);
      const modelo = porLinea.get(linea)?.[n];
      const medidas = base.get(linea);
      if (!modelo || !medidas) continue;
      colocadas.push({
        ...modelo,
        x: p.x,
        y: p.y,
        z: p.z,
        rotated: p.rotated,
        // Se rota desde la medida base, no desde la ya rotada.
        lengthCm: p.rotated ? medidas.widthCm : medidas.lengthCm,
        widthCm: p.rotated ? medidas.lengthCm : medidas.widthCm,
        heightCm: medidas.heightCm,
      });
    }

    const colocadasIds = new Set(colocadas.map((c) => c.id));
    const bandeja = [...layout.placed, ...layout.tray].filter((b) => !colocadasIds.has(b.id));

    layout.applyLayout(colocadas, bandeja);
    setSelectedId(null);
  }, [layout, bounds]);

  /**
   * Avisa que está trabajando antes de largarse a calcular.
   *
   * 🔴 Con 470 bultos el acomodo tarda varios segundos y corre en el hilo
   * principal: la pantalla se congelaba sin ninguna señal, y quien apretó no
   * sabía si el botón había funcionado.
   *
   * El `setTimeout` deja que React pinte "Acomodando…" ANTES del cálculo; sin
   * él, el re-render y el trabajo caen en el mismo cuadro y el aviso no llega
   * a verse nunca.
   *
   * No lo hace más rápido: hace que se entienda que está pasando algo. Sacarlo
   * del hilo principal con un worker es otra tarea.
   */
  const handleAutoArrange = useCallback(() => {
    setArranging(true);
    setTimeout(() => {
      try {
        ejecutarAcomodo();
      } finally {
        // En `finally` para que un error no deje el botón trabado para siempre.
        setArranging(false);
      }
    }, 30);
  }, [ejecutarAcomodo]);

  // ── Guardar ──
  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        positions: agruparPorLinea(layout.placed),
        usedVolumeCbm: layout.stats.usedVolumeCbm,
        usedWeightKg: layout.stats.usedWeightKg,
        fillPercent: layout.stats.fillPercent,
      });
      layout.markSaved();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'No se pudo guardar el acomodo');
    } finally {
      setSaving(false);
    }
  }, [onSave, layout]);

  const selectedBox = selectedId ? layout.placed.find((b) => b.id === selectedId) : null;
  /**
   * La secuencia de carga, agrupada en tandas del mismo producto.
   *
   * Se agrupan solo las CONSECUTIVAS: si el orden dice filtros, impresoras y
   * otra vez filtros, son tres tandas. Juntarlas todas cambiaría el orden en el
   * que hay que cargar, que es justamente lo que la lista informa.
   */
  const sequence = useMemo(() => {
    const tandas: Array<{ id: string; cantidad: number }> = [];
    for (const caja of computeLoadingSequence(layout.placed)) {
      const linea = idDeLinea(caja.id);
      const ultima = tandas[tandas.length - 1];
      if (ultima && idDeLinea(ultima.id) === linea) ultima.cantidad += 1;
      else tandas.push({ id: caja.id, cantidad: 1 });
    }
    return tandas;
  }, [layout.placed]);
  const { stats } = layout;

  return (
    <div className={cn('flex flex-col lg:flex-row gap-4', className)}>
      {/* ── Escena ── */}
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleAutoArrange}
              disabled={arranging}
              className="gap-1.5"
            >
              {arranging ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Wand2 className="h-3.5 w-3.5" />
              )}
              {arranging ? 'Acomodando…' : 'Auto-acomodar'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => selectedId && layout.rotate(selectedId)}
              disabled={!selectedId}
              className="gap-1.5"
            >
              <RotateCw className="w-3.5 h-3.5" />
              Rotar
              <kbd className="ml-1 text-[10px] opacity-60">R</kbd>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (selectedId) {
                  layout.unplace(selectedId);
                  setSelectedId(null);
                }
              }}
              disabled={!selectedId}
              className="gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Quitar
            </Button>

            <div className="w-px h-5 bg-border mx-1" />

            <Button
              size="sm"
              variant="ghost"
              onClick={layout.undo}
              disabled={!layout.canUndo}
              title="Deshacer (Ctrl+Z)"
            >
              <Undo2 className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={layout.redo}
              disabled={!layout.canRedo}
              title="Rehacer (Ctrl+Y)"
            >
              <Redo2 className="w-3.5 h-3.5" />
            </Button>

            <Button
              size="sm"
              onClick={handleSave}
              disabled={!layout.dirty || saving}
              className="gap-1.5 ml-auto"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              {saving ? 'Guardando…' : layout.dirty ? 'Guardar acomodo' : 'Guardado'}
            </Button>
          </div>
        )}

        {saveError && (
          <div className="flex items-start gap-2 text-xs text-bloqueo bg-bloqueo-suave border border-bloqueo-borde rounded-md px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{saveError}</span>
          </div>
        )}

        <div className="relative rounded-lg border bg-gradient-to-b from-slate-50 to-slate-100 overflow-hidden h-[380px] sm:h-[460px] lg:h-[560px]">
          <Canvas shadows dpr={[1, 2]} onPointerMissed={() => setSelectedId(null)}>
            <Suspense fallback={null}>
              <Scene
                status={status}
                bounds={bounds}
                boxes={layout.placed}
                colorOf={colorOf}
                labelOf={labelOf}
                selectedId={selectedId}
                draggingId={draggingId}
                ghost={ghost}
                readOnly={readOnly}
                onSelect={setSelectedId}
                onDragStart={handleDragStart}
                onDragMove={handleDragMove}
                onDragEnd={handleDragEnd}
              />
            </Suspense>
          </Canvas>

          {/* Motivo del rechazo, mientras se arrastra a una posición inválida */}
          {ghost && !ghost.check.valid && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-bloqueo text-white text-xs px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 pointer-events-none">
              <AlertTriangle className="w-3.5 h-3.5" />
              {ghost.check.problems.map(describeProblem).join(' · ')}
            </div>
          )}

          {!readOnly && (
            <div className="absolute top-3 left-3 text-[10px] text-muted-foreground bg-white/80 backdrop-blur-sm rounded px-2 py-1 leading-relaxed pointer-events-none hidden sm:block">
              Arrastrá para mover · <kbd className="font-semibold">R</kbd> rota ·{' '}
              <kbd className="font-semibold">↑↓←→</kbd> ajusta ·{' '}
              <kbd className="font-semibold">Supr</kbd> quita
            </div>
          )}
        </div>
      </div>

      {/* ── Panel ── */}
      <aside className="w-full lg:w-72 shrink-0 flex flex-col gap-3">
        <LoadStatsPanel stats={stats} readOnly={readOnly} />

        {selectedBox && (
          <section className="rounded-lg border bg-card p-3">
            <h4 className="text-xs font-semibold text-muted-foreground mb-2">Seleccionado</h4>
            <p className="text-sm font-medium leading-tight">{labelOf(selectedBox.id)}</p>
            <dl className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <dt>Dimensiones</dt>
                <dd className="font-mono">
                  {selectedBox.lengthCm}×{selectedBox.widthCm}×{selectedBox.heightCm} cm
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>Peso</dt>
                <dd className="font-mono">{selectedBox.weightKg} kg</dd>
              </div>
              <div className="flex justify-between">
                <dt>Posición</dt>
                <dd className="font-mono">
                  X{selectedBox.x} Y{selectedBox.y} Z{selectedBox.z}
                </dd>
              </div>
              {selectedBox.rotated && (
                <div className="flex justify-between text-alerta">
                  <dt>Rotada</dt>
                  <dd>90°</dd>
                </div>
              )}
            </dl>
          </section>
        )}

        {layout.tray.length > 0 && (
          <section className="rounded-lg border border-alerta-borde bg-alerta-suave p-3">
            <h4 className="text-xs font-semibold text-alerta flex items-center gap-1.5 mb-2">
              <PackageOpen className="w-3.5 h-3.5" />
              Sin ubicar ({layout.tray.length})
            </h4>
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {layout.tray.map((box) => (
                <li key={box.id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ backgroundColor: colorOf(box.id) }}
                    />
                    <span className="truncate">{labelOf(box.id)}</span>
                  </span>
                  {!readOnly && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[11px] shrink-0"
                      onClick={() => layout.place(box.id)}
                    >
                      Ubicar
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            {/*
              🔴 Acá decía "Puede requerir uno adicional" y no daba forma de
              crearlo. El aviso nombraba la solución y dejaba a la persona
              buscándola: había que salir a la pestaña Contenedores de la orden,
              abrir el alta, y volver — perdiendo de vista justamente esta lista
              de lo que no entró, que es lo que hay que llevar al nuevo.

              Ahora la cuenta está hecha y el botón está al lado del problema.
            */}
            <p className="mt-2 text-[11px] leading-snug text-alerta">
              Esta carga no entra en el contenedor actual.
              {sobrante && (
                <>
                  {' '}
                  Sobran <strong>{sobrante.volumenCbm} m³</strong> y{' '}
                  <strong>{sobrante.pesoKg.toLocaleString('es-BO')} kg</strong> — harían falta{' '}
                  <strong>
                    {sobrante.contenedoresExtra} contenedor
                    {sobrante.contenedoresExtra > 1 ? 'es' : ''} más
                  </strong>
                  .
                </>
              )}
            </p>

            {!readOnly && onCrearContenedor && (
              <Button
                size="sm"
                variant="outline"
                className="mt-2 h-7 w-full text-[11px]"
                onClick={onCrearContenedor}
              >
                <Plus className="mr-1.5 h-3 w-3" />
                Crear otro contenedor
              </Button>
            )}
          </section>
        )}

        {sequence.length > 0 && (
          <section className="rounded-lg border bg-card p-3">
            <h4 className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 mb-2">
              <Boxes className="w-3.5 h-3.5" />
              Secuencia de carga
            </h4>
            {/*
              Agrupada por tandas del mismo producto.

              🔴 Se listaba un renglón POR BULTO: con 470 la lista decía
              "Teclado Mecánico USB Ergonómico" 200 veces, en 470 nodos del DOM
              que nadie lee. Quien carga el contenedor necesita saber "primero
              van 12 filtros, después 5 impresoras", no numerar cada caja.

              Agrupar las tandas consecutivas la vuelve legible y baja de 470
              renglones a unas pocas decenas.
            */}
            <ol className="max-h-48 space-y-1 overflow-y-auto text-xs">
              {sequence.map((tanda, index) => (
                <li key={`${tanda.id}-${index}`} className="flex items-center gap-2">
                  <span className="w-4 shrink-0 text-right tabular-nums text-muted-foreground">
                    {index + 1}
                  </span>
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: colorOf(tanda.id) }}
                  />
                  <span className="truncate">{labelOf(tanda.id)}</span>
                  {tanda.cantidad > 1 && (
                    <span className="ml-auto shrink-0 font-mono text-muted-foreground">
                      ×{tanda.cantidad}
                    </span>
                  )}
                </li>
              ))}
            </ol>
            <p className="mt-2 text-[11px] text-muted-foreground leading-snug">
              De atrás hacia adelante y de abajo hacia arriba.
            </p>
          </section>
        )}
      </aside>
    </div>
  );
}

// ── Panel de métricas ────────────────────────────────────────────────────────

function LoadStatsPanel({
  stats,
  readOnly,
}: {
  stats: ReturnType<typeof useContainerLayout>['stats'];
  /**
   * Con el armado cerrado, los avisos dejan de pedir una acción.
   *
   * "Conviene redistribuir" sobre una carga ya entregada es un consejo imposible
   * de seguir — no hay nada que redistribuir, el contenedor viajó así. El dato
   * sigue sirviendo para entender qué pasó; el imperativo no.
   */
  readOnly?: boolean;
}) {
  return (
    <section className="rounded-lg border bg-card p-3 space-y-3">
      <Meter
        label="Volumen"
        value={stats.fillPercent}
        detail={`${stats.usedVolumeCbm} / ${stats.containerVolumeCbm} m³`}
        tone={stats.fillPercent > 95 ? 'warn' : 'ok'}
      />
      <Meter
        label="Peso"
        value={stats.weightPercent}
        detail={
          stats.maxWeightKg > 0
            ? `${stats.usedWeightKg} / ${stats.maxWeightKg} kg`
            : `${stats.usedWeightKg} kg`
        }
        tone={stats.overweight ? 'danger' : stats.weightPercent > 90 ? 'warn' : 'ok'}
      />

      {stats.usedWeightKg > 0 && (
        <div>
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
            <span>Frente {stats.frontLoadPercent}%</span>
            <span>Fondo {stats.rearLoadPercent}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden flex bg-muted">
            <div className="bg-info" style={{ width: `${stats.frontLoadPercent}%` }} />
            <div className="bg-info" style={{ width: `${stats.rearLoadPercent}%` }} />
          </div>
        </div>
      )}

      {stats.overweight && (
        <Alert>
          {readOnly
            ? 'Este contenedor viajó por encima del peso máximo.'
            : 'Supera el peso máximo del contenedor. Hay que quitar carga.'}
        </Alert>
      )}
      {stats.unbalanced && !stats.overweight && (
        <Alert tone="warn">
          {readOnly
            ? 'La carga quedó desbalanceada entre el frente y el fondo.'
            : 'Carga desbalanceada. Conviene redistribuir para no exceder el peso por eje.'}
        </Alert>
      )}
    </section>
  );
}

function Meter({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: number;
  detail: string;
  tone: 'ok' | 'warn' | 'danger';
}) {
  const barColor = tone === 'danger' ? 'bg-bloqueo' : tone === 'warn' ? 'bg-alerta' : 'bg-ok';
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-xs font-medium">{label}</span>
        <span className="text-xs tabular-nums text-muted-foreground">{value.toFixed(0)}%</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-[width] duration-200', barColor)}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">{detail}</p>
    </div>
  );
}

function Alert({
  children,
  tone = 'danger',
}: {
  children: React.ReactNode;
  tone?: 'danger' | 'warn';
}) {
  return (
    <p
      className={cn(
        'flex items-start gap-1.5 text-[11px] rounded-md px-2 py-1.5 leading-snug',
        tone === 'danger'
          ? 'text-bloqueo bg-bloqueo-suave border border-bloqueo-borde'
          : 'text-alerta bg-alerta-suave border border-alerta-borde'
      )}
    >
      <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
      <span>{children}</span>
    </p>
  );
}
